import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { formatLocalDateTime } from "utils/dateFormatter";

interface ExerciseVideoCardProps {
    video: {
        seqId: number;
        title: string;
        description?: string;
        youtubeUrl: string;
        thumbnailUrl?: string;
        durationSec?: number;
        category?: string;
        difficultyLevel?: string;
        isActive: boolean;
        isFeatured?: boolean;
        viewCount?: number;
        likeCount?: number;
        tenantName?: string;
        crtDt: string;
        bodyPartNames?: string[];
    };
    onClick: (video: any) => void;
    onEdit: (video: any) => void;
    onDelete: () => void;
    darkMode?: boolean;
}

function ExerciseVideoCard({ video, onClick, onEdit, onDelete, darkMode }: ExerciseVideoCardProps) {
    const formatDuration = (seconds?: number) => {
        if (!seconds) return "-";
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const getCategoryLabel = (category?: string) => {
        const labels: Record<string, string> = {
            'KNEE': '무릎',
            'HIP': '고관절',
            'BACK': '허리',
            'SHOULDER': '어깨',
        };
        return category ? labels[category] || category : '-';
    };

    const getDifficultyLabel = (level?: string) => {
        const labels: Record<string, string> = {
            'BEGINNER': '초급',
            'INTERMEDIATE': '중급',
            'ADVANCED': '고급',
        };
        return level ? labels[level] || level : '-';
    };

    const getDifficultyColor = (level?: string) => {
        switch (level) {
            case 'BEGINNER':
                return 'success';
            case 'INTERMEDIATE':
                return 'warning';
            case 'ADVANCED':
                return 'error';
            default:
                return 'info';
        }
    };

    return (
        <Card
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                transition: "all 0.2s ease-in-out",
                cursor: "pointer",
                border: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid rgba(0, 0, 0, 0.12)",
                boxShadow: darkMode
                    ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                    : "0 2px 8px rgba(0, 0, 0, 0.08)",
                "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: darkMode
                        ? "0 8px 20px rgba(255, 255, 255, 0.15)"
                        : "0 8px 20px rgba(0, 0, 0, 0.15)",
                    borderColor: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)",
                },
            }}
            onClick={() => onClick(video)}
        >
            {/* 썸네일 영역 */}
            <MDBox
                sx={{
                    position: "relative",
                    width: "100%",
                    paddingTop: "56.25%", // 16:9 비율
                    backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)",
                    backgroundImage: video.thumbnailUrl ? `url(${video.thumbnailUrl})` : "none",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                {!video.thumbnailUrl && (
                    <MDBox
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                        }}
                    >
                        <Icon sx={{ fontSize: 48, color: darkMode ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)" }}>
                            play_circle_outline
                        </Icon>
                    </MDBox>
                )}
                {video.durationSec && (
                    <MDBox
                        sx={{
                            position: "absolute",
                            bottom: 8,
                            right: 8,
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                            borderRadius: 1,
                            px: 1,
                            py: 0.5,
                        }}
                    >
                        <MDTypography variant="caption" sx={{ color: "#fff", fontWeight: "medium" }}>
                            {formatDuration(video.durationSec)}
                        </MDTypography>
                    </MDBox>
                )}
                {video.isFeatured && (
                    <MDBox
                        sx={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                        }}
                    >
                        <Chip
                            icon={<Icon sx={{ fontSize: "16px !important" }}>star</Icon>}
                            label="추천"
                            size="small"
                            sx={{
                                backgroundColor: "#FFD700",
                                color: "#000",
                                fontWeight: "bold",
                            }}
                        />
                    </MDBox>
                )}
            </MDBox>

            {/* 콘텐츠 영역 */}
            <MDBox p={2.5} display="flex" flexDirection="column" flex={1}>
                <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                    <MDTypography variant="h6" fontWeight="bold" sx={{ flex: 1, pr: 1 }}>
                        {video.title}
                    </MDTypography>
                    <Chip
                        label={video.isActive ? "활성" : "비활성"}
                        color={video.isActive ? "success" : "error"}
                        size="small"
                        sx={{
                            fontWeight: "medium",
                            minWidth: 60,
                            ...(video.isActive && {
                                backgroundColor: "#4CAF50",
                                color: "#ffffff",
                                "& .MuiChip-label": {
                                    color: "#ffffff",
                                },
                            }),
                        }}
                    />
                </MDBox>

                {video.description && (
                    <MDTypography
                        variant="body2"
                        color="text"
                        sx={{
                            mb: 2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                        }}
                    >
                        {video.description}
                    </MDTypography>
                )}

                <Divider sx={{ my: 1.5 }} />

                <MDBox display="flex" gap={1} mb={1.5} flexWrap="wrap">
                    {video.bodyPartNames && video.bodyPartNames.length > 0 ? (
                        video.bodyPartNames.map((name: string, index: number) => (
                            <Chip
                                key={index}
                                label={name}
                                size="small"
                                variant="outlined"
                                sx={{
                                    fontSize: "0.75rem",
                                    color: darkMode ? "rgba(255, 255, 255, 0.8)" : undefined,
                                    borderColor: darkMode ? "rgba(255, 255, 255, 0.3)" : undefined,
                                }}
                            />
                        ))
                    ) : (
                        <MDTypography variant="caption" color="text.secondary">
                            -
                        </MDTypography>
                    )}
                </MDBox>

                <MDBox display="flex" gap={2} mb={1}>
                    <MDBox display="flex" alignItems="center">
                        <Icon fontSize="small" sx={{ mr: 0.5, color: darkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)" }}>
                            visibility
                        </Icon>
                        <MDTypography variant="caption" color="text.secondary">
                            {video.viewCount || 0}
                        </MDTypography>
                    </MDBox>
                </MDBox>

                <MDBox display="flex" alignItems="center" justifyContent="space-between" mt="auto">
                    <MDBox display="flex" alignItems="center">
                        <Icon fontSize="small" sx={{ mr: 1, color: darkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)" }}>
                            schedule
                        </Icon>
                        <MDTypography variant="caption" color="text.secondary">
                            {formatLocalDateTime(video.crtDt)}
                        </MDTypography>
                    </MDBox>
                    <MDBox display="flex" gap={0.5}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit(video);
                            }}
                            sx={{
                                color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                "&:hover": {
                                    backgroundColor: darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.04)",
                                    color: "#49a3f1",
                                },
                            }}
                        >
                            <Icon fontSize="small">edit</Icon>
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete();
                            }}
                            sx={{
                                color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                "&:hover": {
                                    backgroundColor: darkMode ? "rgba(255, 100, 100, 0.1)" : "rgba(255, 0, 0, 0.04)",
                                    color: "#f44336",
                                },
                            }}
                        >
                            <Icon fontSize="small">delete</Icon>
                        </IconButton>
                    </MDBox>
                </MDBox>
            </MDBox>
        </Card>
    );
}

export default ExerciseVideoCard;
